import React, { useEffect, useState } from 'react'
import { createStripeCustomer, subscribe, buySingleReport, getBillingPortal, getPrices, type BillingStatus, type PlanPrices } from '../../lib/billingApi'
import CheckoutModal from './CheckoutModal'
import ReportPackPicker from './ReportPackPicker'
import { availableReportPacks, formatUsd, type ReportPackId } from '../../lib/reportPacks'
import { analytics } from '../../lib/analytics'

interface UpgradeModalProps {
  open: boolean
  onClose: () => void
  onUpgraded?: () => void
  currentPlan?: 'free' | 'pro2' | 'pro5'
  billingStatus?: BillingStatus | null
  trigger?: 'limit_reached' | 'settings' | 'banner' | 'profile'
  userCreatedAt?: string
}

const BASE_PLANS = [
  {
    id: 'single' as const,
    name: 'Single Report',
    price: '$20',
    priceCents: 2000,
    period: 'one-time',
    billingCycle: 'one_time' as const,
    features: [
      'Full-depth Intelligence Report',
      'All source analysis included',
      'No subscription required',
    ],
    highlight: false,
  },
  {
    id: 'pro2' as const,
    name: 'Pro-2',
    price: '$30',
    priceCents: 3000,
    period: '/month',
    billingCycle: 'monthly' as const,
    features: [
      '2 Intelligence Reports per month',
      'Full-depth analysis',
      'Priority processing',
    ],
    highlight: false,
  },
  {
    id: 'pro5' as const,
    name: 'Pro-5',
    price: '$72',
    priceCents: 7200,
    period: '/month',
    billingCycle: 'monthly' as const,
    features: [
      '5 Intelligence Reports per month',
      'Full-depth analysis',
      'Priority processing & support',
    ],
    highlight: true,
  },
]

function applyDynamicPrices(
  plans: typeof BASE_PLANS,
  prices: PlanPrices,
) {
  return plans.map((plan) => {
    const cents = prices[plan.id]
    if (cents == null) return plan
    const dollars = Math.floor(cents / 100)
    const cents_remainder = cents % 100
    const formatted = cents_remainder === 0 ? `$${dollars}` : `$${dollars}.${String(cents_remainder).padStart(2, '0')}`
    return { ...plan, priceCents: cents, price: formatted }
  })
}

export default function UpgradeModal({
  open,
  onClose,
  onUpgraded,
  currentPlan = 'free',
  billingStatus,
  trigger = 'limit_reached',
  userCreatedAt,
}: UpgradeModalProps) {
  const [loading, setLoading] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [checkoutSecret, setCheckoutSecret] = useState<string | null>(null)
  const [checkoutSessionId, setCheckoutSessionId] = useState<string | null>(null)
  const [checkoutPublishableKey, setCheckoutPublishableKey] = useState<string | null>(null)
  const [checkoutPlanId, setCheckoutPlanId] = useState<'single' | 'pro2' | 'pro5' | null>(null)
  const [dynamicPrices, setDynamicPrices] = useState<PlanPrices>({})
  const [reportPack, setReportPack] = useState<ReportPackId>('single')

  const PLANS = applyDynamicPrices(BASE_PLANS, dynamicPrices)
  const reportPacks = availableReportPacks(dynamicPrices)
  const selectedReportPack = reportPacks.find(pack => pack.id === reportPack) ?? reportPacks[0]
  // Analytics for one-time purchases follow the selected pack (single, 2 or 3 reports).
  const purchaseDetails = (planId: 'single' | 'pro2' | 'pro5') => {
    const plan = PLANS.find(p => p.id === planId)
    if (planId === 'single' && selectedReportPack) {
      return { plan: selectedReportPack.id, priceCents: selectedReportPack.priceCents, quantity: selectedReportPack.reports, billingCycle: plan?.billingCycle }
    }
    return { plan: planId, priceCents: plan?.priceCents, quantity: undefined, billingCycle: plan?.billingCycle }
  }

  useEffect(() => {
    getPrices().then(setDynamicPrices).catch(() => {})
  }, [])

  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      return () => { document.body.style.overflow = prev }
    }
  }, [open])

  const lifetimeFreeUsed = billingStatus?.lifetime_free_used ?? true
  const reservedFree = billingStatus?.reserved_free_report_count ?? 0
  const monthlyUsed = billingStatus?.monthly_report_used ?? 0
  const monthlyLimit = billingStatus?.monthly_report_limit ?? 0
  const reservedMonthly = billingStatus?.reserved_monthly_report_count ?? 0
  const availableMonthly = billingStatus?.available_monthly_report_count ?? Math.max(0, monthlyLimit - monthlyUsed - reservedMonthly)
  const signupDateMs = userCreatedAt ? new Date(userCreatedAt).getTime() : Number.NaN
  const daysSinceSignup = Number.isFinite(signupDateMs)
    ? Math.max(0, Math.floor((Date.now() - signupDateMs) / (1000 * 60 * 60 * 24)))
    : 0
  const totalResearch = monthlyUsed + reservedMonthly + reservedFree + (lifetimeFreeUsed ? 1 : 0)

  useEffect(() => {
    if (!open) return

    analytics.trackUpgradeModalViewed({
      trigger,
      current_plan: currentPlan,
      days_since_signup: daysSinceSignup,
      total_research: totalResearch,
    })
  }, [currentPlan, daysSinceSignup, open, totalResearch, trigger])

  if (!open) return null

  let headerMessage = 'Unlock Intelligence Reports'
  if (currentPlan === 'free' && reservedFree > 0) {
    headerMessage = "You already have a report waiting for approval"
  } else if (currentPlan === 'free' && lifetimeFreeUsed) {
    headerMessage = "You've used your free Intelligence Report"
  } else if (currentPlan === 'pro2' || currentPlan === 'pro5') {
    if (availableMonthly <= 0) {
      headerMessage = `You've used all ${monthlyLimit} reports in your plan this month`
    } else if (reservedMonthly > 0) {
      headerMessage = 'Approve or cancel your pending report to free up reserved balance'
    }
  }

  const handlePurchase = async (planId: 'single' | 'pro2' | 'pro5') => {
    setError(null)
    setLoading(planId)
    setCheckoutPlanId(planId)
    const details = purchaseDetails(planId)
    analytics.trackUpgradeStarted({
      target_plan: details.plan,
      current_plan: currentPlan,
      trigger,
      price_cents: details.priceCents,
      billing_cycle: details.billingCycle,
      ...(details.quantity ? { quantity: details.quantity } : {}),
    })
    try {
      await createStripeCustomer()
      const result = planId === 'single'
        ? await buySingleReport(1, selectedReportPack?.id ?? 'single')
        : await subscribe(planId)
      if (result.clientSecret) {
        setCheckoutSecret(result.clientSecret)
        setCheckoutSessionId(result.sessionId)
        setCheckoutPublishableKey(result.publishableKey ?? null)
      } else {
        setError('Unable to create payment session.')
        setCheckoutPlanId(null)
      }
    } catch (e: any) {
      setError(e?.message || 'Purchase failed. Please try again.')
      setCheckoutPlanId(null)
    } finally {
      setLoading(null)
    }
  }

  if (checkoutSecret) {
    const checkoutDetails = checkoutPlanId ? purchaseDetails(checkoutPlanId) : null
    return (
      <CheckoutModal
        clientSecret={checkoutSecret}
        sessionId={checkoutSessionId}
        publishableKey={checkoutPublishableKey}
        onClose={() => {
          setCheckoutPlanId(null)
          setCheckoutSecret(null)
          setCheckoutSessionId(null)
          setCheckoutPublishableKey(null)
          onClose()
        }}
        onComplete={() => {
          if (checkoutDetails?.priceCents != null && checkoutDetails.billingCycle) {
            analytics.trackUpgradeCompleted({
              plan: checkoutDetails.plan,
              price_cents: checkoutDetails.priceCents,
              billing_cycle: checkoutDetails.billingCycle,
              ...(checkoutDetails.quantity ? { quantity: checkoutDetails.quantity } : {}),
              previous_plan: currentPlan,
              days_as_free_user: currentPlan === 'free' ? daysSinceSignup : 0,
              research_before_upgrade: totalResearch,
            })
          }

          setCheckoutPlanId(null)
          setCheckoutSecret(null)
          setCheckoutSessionId(null)
          setCheckoutPublishableKey(null)
          onUpgraded?.()
          onClose()
        }}
      />
    )
  }

  return (
    <div className="fixed inset-0 z-[10000] flex items-end md:items-center justify-center bg-black/40 overflow-y-auto overscroll-contain">
      <div className="bg-white dark:bg-gray-900 rounded-t-3xl md:rounded-3xl shadow-2xl w-full max-w-4xl p-6 md:p-8 border border-gray-200/50 dark:border-gray-700/50 max-h-[95dvh] overflow-y-auto">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">{headerMessage}</h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1">Choose how you'd like to continue with Intelligence Reports.</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-xl bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {PLANS.map((plan) => {
            const isCurrent = plan.id === currentPlan
            return (
              <div
                key={plan.id}
                className={`rounded-2xl p-6 ${
                  plan.highlight
                    ? 'border-2 border-blue-500/60 bg-gradient-to-b from-gray-900 to-gray-800 dark:from-gray-800 dark:to-gray-900'
                    : 'border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <h3 className={`text-xl font-bold ${plan.highlight ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
                    {plan.name}
                  </h3>
                  {plan.highlight && (
                    <span className="px-3 py-1 text-xs font-bold text-white bg-blue-500 rounded-full">BEST VALUE</span>
                  )}
                </div>
                <p className={`mb-5 ${plan.highlight ? 'text-gray-400' : 'text-gray-500 dark:text-gray-400'}`}>
                  {plan.id === 'single' && selectedReportPack ? formatUsd(selectedReportPack.priceCents) : plan.price}<span className="text-sm">{plan.period}</span>
                </p>
                {plan.id === 'single' && (
                  <div className="mb-5">
                    <ReportPackPicker options={reportPacks} value={selectedReportPack?.id ?? 'single'} onChange={setReportPack} />
                  </div>
                )}
                <ul className="space-y-3 text-sm">
                  {plan.features.map((f) => (
                    <li key={f} className={`flex items-center gap-3 ${plan.highlight ? 'text-gray-200' : 'text-gray-700 dark:text-gray-300'}`}>
                      <svg className="w-4 h-4 flex-shrink-0 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  disabled={loading !== null || isCurrent}
                  onClick={() => handlePurchase(plan.id)}
                  className={`mt-6 w-full py-3 rounded-xl font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    plan.highlight
                      ? 'bg-blue-500 text-white hover:bg-blue-600'
                      : 'bg-gray-900 dark:bg-gray-700 text-white hover:bg-gray-800 dark:hover:bg-gray-600 border border-gray-300 dark:border-gray-600'
                  }`}
                >
                  {loading === plan.id
                    ? 'Processing...'
                    : isCurrent
                      ? 'Current plan'
                      : plan.id === 'single'
                        ? selectedReportPack && selectedReportPack.reports > 1 ? `Buy ${selectedReportPack.reports} Reports` : 'Buy Report'
                        : 'Subscribe'}
                </button>
              </div>
            )
          })}
        </div>

        <div className="mt-6 flex items-center justify-between">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Founding / Beta pricing. Cancel subscriptions anytime. Taxes may apply.
          </p>
          {currentPlan !== 'free' && (
            <button
              onClick={async () => {
                try {
                  const url = await getBillingPortal({ returnUrl: window.location.href })
                  window.location.assign(url)
                } catch {
                  setError('Failed to open billing portal.')
                }
              }}
              className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
            >
              Open Billing Portal
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
